import { scaleLinear, scaleTime } from 'd3-scale';
import { line } from 'd3-shape';
import { useMemo, useRef, useState, type PointerEvent } from 'react';
import { nearestIndex, type ChartPoint } from '../../../lib/metrics';
import { axisTimeFormatter, formatDateTime } from '../../../lib/time';
import { useElementWidth } from './useElementWidth';

export interface ServerSeries {
  id: string;
  label: string;
  /** A `var(--series-N)` token so light/dark themes stay in sync. */
  color: string;
  points: ChartPoint[];
  /** This server's own alert threshold for the metric, if it has one. */
  threshold?: number;
}

interface MultiServerChartProps {
  series: ServerSeries[];
  fromMs: number;
  toMs: number;
  binMs: number;
  domain: [number, number];
  formatValue: (value: number) => string;
  formatTick: (value: number) => string;
  ariaLabel: string;
  /** Total height including the x-axis band. */
  height?: number;
}

const DEFAULT_HEIGHT = 220;
const MARGIN = { top: 12, right: 12, bottom: 26, left: 48 };

/** Nulls break the line where polls are missing, so gaps read as gaps rather than a straight join. */
function withGaps(points: ChartPoint[], binMs: number): (ChartPoint | null)[] {
  const out: (ChartPoint | null)[] = [];
  points.forEach((p, i) => {
    const prev = points[i - 1];
    if (prev && p.t - prev.t > binMs * 1.5) out.push(null);
    out.push(p);
  });
  return out;
}

/**
 * Compares the same metric across several servers: one line per server (fixed categorical
 * colour, never reassigned by the visible set), each with its own dashed threshold tick.
 */
export function MultiServerChart({ series, fromMs, toMs, binMs, domain, formatValue, formatTick, ariaLabel, height = DEFAULT_HEIGHT }: MultiServerChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(containerRef);
  const [hoverT, setHoverT] = useState<number | null>(null);

  const plotRight = Math.max(MARGIN.left + 10, width - MARGIN.right);
  const plotBottom = height - MARGIN.bottom;

  const x = useMemo(() => scaleTime().domain([fromMs, toMs]).range([MARGIN.left, plotRight]), [fromMs, toMs, plotRight]);
  const y = useMemo(() => scaleLinear().domain(domain).range([plotBottom, MARGIN.top]).nice(4), [domain, plotBottom]);

  const lineGen = useMemo(
    () =>
      line<ChartPoint | null>()
        .defined((p): p is ChartPoint => p !== null)
        .x((p) => x(p!.t))
        .y((p) => y(p!.avg)),
    [x, y],
  );

  const tickLabel = axisTimeFormatter(toMs - fromMs);
  const xTicks = x
    .ticks(Math.max(2, Math.floor((plotRight - MARGIN.left) / 110)))
    .filter((tick, i, all) => i === 0 || tickLabel(tick) !== tickLabel(all[i - 1]!));
  const yTicks = y.ticks(height < 180 ? 2 : 4);
  const [lo, hi] = domain;

  function onPointerMove(event: PointerEvent) {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHoverT(x.invert(event.clientX - rect.left).getTime());
  }

  const hovered =
    hoverT !== null
      ? series
          .map((s) => {
            const idx = nearestIndex(s.points, hoverT);
            return idx !== null ? { series: s, point: s.points[idx]! } : null;
          })
          .filter((row): row is { series: ServerSeries; point: ChartPoint } => row !== null)
          .sort((a, b) => b.point.avg - a.point.avg)
      : null;

  const tooltipLeft = hoverT !== null ? Math.min(Math.max(x(hoverT) + 10, 0), Math.max(0, width - 190)) : 0;

  return (
    <div className="flex flex-col gap-3">
      <div
        ref={containerRef}
        className="relative w-full touch-pan-y"
        style={{ height }}
        onPointerMove={onPointerMove}
        onPointerLeave={() => setHoverT(null)}
      >
        {width > 0 && (
          <svg width={width} height={height} role="img" aria-label={ariaLabel} className="block overflow-visible font-mono text-[10.5px] [&_text]:fill-fg-muted">
            <g className="stroke-grid [shape-rendering:crispEdges]">
              {yTicks.map((tick) => (
                <g key={tick}>
                  <line x1={MARGIN.left} x2={plotRight} y1={y(tick)} y2={y(tick)} />
                  <text x={MARGIN.left - 8} y={y(tick)} dy="0.32em" textAnchor="end" className="stroke-none">
                    {formatTick(tick)}
                  </text>
                </g>
              ))}
            </g>

            <line className="stroke-axis [shape-rendering:crispEdges]" x1={MARGIN.left} x2={plotRight} y1={plotBottom} y2={plotBottom} />
            {xTicks.map((tick) => (
              <text key={tick.getTime()} x={x(tick)} y={plotBottom + 17} textAnchor="middle">
                {tickLabel(tick)}
              </text>
            ))}

            {series.map(
              (s) =>
                s.threshold !== undefined &&
                s.threshold >= lo &&
                s.threshold <= hi && (
                  <line
                    key={`${s.id}-threshold`}
                    x1={MARGIN.left}
                    x2={plotRight}
                    y1={y(s.threshold)}
                    y2={y(s.threshold)}
                    stroke={s.color}
                    strokeOpacity={0.55}
                    className="[stroke-dasharray:4_3] [shape-rendering:crispEdges]"
                  />
                ),
            )}

            {series.map((s) => {
              const last = s.points[s.points.length - 1];
              return (
                <g key={s.id}>
                  <path d={lineGen(withGaps(s.points, binMs)) ?? ''} fill="none" stroke={s.color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  {last && <circle cx={x(last.t)} cy={y(last.avg)} r={3} fill={s.color} className="stroke-surface stroke-2" />}
                </g>
              );
            })}

            {hoverT !== null && <line x1={x(hoverT)} x2={x(hoverT)} y1={MARGIN.top} y2={plotBottom} className="stroke-fg-muted [shape-rendering:crispEdges]" />}

            <rect
              className="cursor-crosshair fill-transparent"
              x={MARGIN.left}
              y={0}
              width={Math.max(0, plotRight - MARGIN.left)}
              height={plotBottom}
            />
          </svg>
        )}

        {hovered && hovered.length > 0 && (
          <div
            className="pointer-events-none absolute top-2 z-10 flex min-w-[170px] flex-col gap-1 rounded-lg border border-line bg-surface px-3 py-2 text-xs shadow-popover"
            style={{ left: tooltipLeft }}
          >
            <div className="font-mono text-[11px] text-fg-muted">{formatDateTime(hovered[0]!.point.t)}</div>
            {hovered.map(({ series: s, point }) => (
              <div key={s.id} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-1.5 truncate text-fg-secondary">
                  <span className="size-2 flex-none rounded-full" style={{ background: s.color }} aria-hidden />
                  {s.label}
                </span>
                <span className="flex-none font-mono font-medium">{formatValue(point.avg)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-fg-secondary">
        {series.map((s) => (
          <span key={s.id} className="flex items-center gap-1.5">
            <span className="size-2.5 flex-none rounded-full" style={{ background: s.color }} aria-hidden />
            {s.label}
            {s.threshold !== undefined && <span className="text-fg-muted">· limit {formatValue(s.threshold)}</span>}
          </span>
        ))}
      </div>
    </div>
  );
}
